<template>
  <div>
    <my-header></my-header>

    <div
      v-if="isLoadedNormal"
      class="content-container-level-1"
    >
      <h2>New game event</h2>

      <form @submit.prevent="createGvntWithConfirmation">

        <h5>Type</h5>
        <div class="form-group">
          <b-form-select
            v-model="type"
            :options="typeOptions"
          />
        </div>

        <hr>

        <h5>Segment</h5>
        <div class="form-group">
          <b-form-select
            id="player-group-selector"
            v-model="playerGroupId"
            :options="playerGroupOptions"
          />
        </div>

        <hr>

        <h5>Period</h5>
        <table class="period-table">
          <tr>
            <td>Start</td>
            <td class="date">
              <b-form-datepicker
                v-model="plannedStartDate"
                :min="minPlannedStartDate"
                :start-weekday="MONDAY"
              />
            </td>
            <td>
              <time-input v-model="plannedStartTime"/>
            </td>
          </tr>
          <tr>
            <td>Finish</td>
            <td class="date">
              <b-form-datepicker
                v-model="plannedFinishDate"
                :min="minPlannedFinishDate"
                :start-weekday="MONDAY"
              />      
            </td>
            <td>
              <time-input v-model="plannedFinishTime"/>
            </td>
          </tr>
          <tr>
            <td>Duration</td>
            <td colspan="2" class="pl-3">{{durationStr}}</td>
          </tr>
        </table>

        <hr>

        <h5>Client min version</h5>
        <table class="client-min-version-range mb-2">
          <tr>
            <td>Max value (server version):</td>
            <td>{{newGvntInfo.gameVersion}}</td>
          </tr>
          <tr>
            <td>Min value (forced version):</td>
            <td>{{newGvntInfo.forcedVersion}}</td>
          </tr>
        </table>
        <p class="mb-2">
          {{clientMinVersionComment}}
        </p>
        <input class="form-control" v-model="clientMinVersion" required>

        <hr>

        <b-form-checkbox
          id="checkbox-starts-only-once"
          v-model="startsOnlyOnce"
          class="mb-3"
        >
          Starts only once
        </b-form-checkbox>

        <hr>

        <b-button :to="{name: 'gvnts'}">Cancel</b-button>
        <b-button type="submit" variant="primary">Create</b-button>
      </form>
    </div>

    <loading-indicator
      v-if="isLoading"
    />
  </div>
</template>

<script>
/**
 * @typedef { import('../model/event.js').PlayerGroupId } PlayerGroupId
 */
import { DEFAULT_PLAYER_GROUP_ID      } from '../model/event.js';
import { getPlayerGroupOptionsVerbose } from '../model/event.js';

import { GVNT_TYPE_FLOWER_FEST }  from '../model/gvnt.js';
import { getGvntTypeOptions    }  from '../model/gvnt.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { floorMomentTo5Min                   } from '../util.js';
import { convertMomentToDataStr              } from '../util.js';
import { convertMomentToTimeStr              } from '../util.js';
import { getDurationStrBetweenMoments        } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';
import { normalizeVersionStr                 } from '../util.js';

import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import TimeInput from './TimeInput.vue';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'GvntAdd',

  components: {
    MyHeader,
    LoadingIndicator,
    TimeInput,
  },

  data() {
    const now = new Date();

    const start = floorMomentTo5Min(now);
    const startDateStr = convertMomentToDataStr(start);
    const startTimeStr = convertMomentToTimeStr(start);

    // By default game event duration is 48 hours (2 days).
    const DEFAULT_DURATION_HOURS = 48;

    const finish = new Date(start.getTime());
    finish.setHours(finish.getHours() + DEFAULT_DURATION_HOURS);
    const finishDateStr = convertMomentToDataStr(finish);
    const finishTimeStr = convertMomentToTimeStr(finish);

    return {
      MONDAY: 1,

      loadState: LOAD_STATE_INIT,
      newGvntInfo: null,

      type: GVNT_TYPE_FLOWER_FEST,
      typeOptions: getGvntTypeOptions(),

      plannedStartDate:     startDateStr,
      minPlannedStartDate:  startDateStr,
      plannedStartTime:     startTimeStr,

      plannedFinishDate:    finishDateStr,
      minPlannedFinishDate: startDateStr,
      plannedFinishTime:    finishTimeStr,

      clientMinVersion: '',

      playerGroupId:      DEFAULT_PLAYER_GROUP_ID,
      playerGroupOptions: getPlayerGroupOptionsVerbose(),

      startsOnlyOnce: false
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?Date} */
    plannedStartMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedStartDate, this.plannedStartTime);
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDate, this.plannedFinishTime);
    },

    /** @returns {?string} */
    durationStr() {
      return getDurationStrBetweenMoments(this.plannedStartMoment, this.plannedFinishMoment);
    },

    /** @returns {?string} */
    clientMinVersionComment() {
      const newGvntInfo = this.newGvntInfo;
      if(newGvntInfo === null) {
        return null;
      }

      if(newGvntInfo.gameVersion === newGvntInfo.forcedVersion) {
        let message = '';
        message += 'Client min version of new game event must be inside this range. ';
        message += 'Server version and forced version are equal. '
        message += 'Consequently, value ' + newGvntInfo.gameVersion + ' is the only option for min client version.';
        return message;
      }

      return 'Specify client min version of new game event inside this range.';
    },
  },

  created() {
    this.loadNewGvntInfo();
  },

  methods: {
    loadNewGvntInfo() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      HTTP.get('/new_gvnt_info')
      .then(response => {
        const data = response.data.data;
        const gameVersion = normalizeVersionStr(data.gameVersion);
        const forcedVersion = normalizeVersionStr(data.forcedVersion);

        this.newGvntInfo = {
          gameVersion,
          forcedVersion,
        };

        this.clientMinVersion = forcedVersion;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e.response.data, {sender: this});
      });
    },

    createGvntWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const gvntDefInputReport = this.getGvntDefInputReport();

      if(gvntDefInputReport.error !== null) {
        notifier.showError(gvntDefInputReport.error, {sender: this});
        return;
      }

      const gvntDef = gvntDefInputReport.gvntDef;
      if(gvntDef === null) {
        notifier.showError('Invalid input report. gvntDef === null.', {sender: this});
        return;
      }

      this.confirmGvntCreation(() => {
        this.createGvnt(gvntDef);
      });
    },

    getGvntDefInputReport() {
      const plannedStartMoment = this.plannedStartMoment;
      if(plannedStartMoment === null) {
        return new GvntDefInputReport(
          null,
          new Error('plannedStartMoment === null')
        );
      }
      const plannedStartSec  = Math.floor(plannedStartMoment.getTime() / 1000);

      const plannedFinishMoment = this.plannedFinishMoment;
      if(plannedFinishMoment === null) {
        return new GvntDefInputReport(
          null,
          new Error('plannedFinishMoment === null')
        );
      }
      const plannedFinishSec = Math.floor(plannedFinishMoment.getTime() / 1000);

      if(plannedStartSec === plannedFinishSec) {
        return new GvntDefInputReport(
          null,
          new Error('Contest duration is zero.')
        );
      }

      if(plannedFinishSec < plannedStartSec) {
        return new GvntDefInputReport(
          null,
          new Error('Invalid period. Finish before start.')
        );
      }

      const trimmedClientMinVersion = this.clientMinVersion.trim();
      if(trimmedClientMinVersion.length === 0) {
        return new GvntDefInputReport(
          null,
          new Error('Client min version must be specified.')
        );
      }

      const gvntDef = new GvntDef(
        this.type,
        plannedStartSec,
        plannedFinishSec,
        trimmedClientMinVersion,
        this.playerGroupId,
        this.startsOnlyOnce
      );
      return new GvntDefInputReport(gvntDef, null);
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmGvntCreation(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to create this game event {env}?',
        'Create',
        onConfirmed,
      );
    },

    /**
     * @param {GvntDef} gvntDef
     */
    createGvnt(gvntDef) {
      const startsOnlyOnceNum = Number(gvntDef.startsOnlyOnce);

      const data = new FormData();
      data.set('type',               gvntDef.type.toString());
      data.set('stime',              gvntDef.plannedStartSec.toString());
      data.set('ftime',              gvntDef.plannedFinishSec.toString());
      data.set('client_min_version', gvntDef.clientMinVersion);
      data.set('player_group_id',    gvntDef.playerGroupId.toString());
      data.set('starts_only_once',   startsOnlyOnceNum.toString());

      HTTP.post('/gvnt/add', data)
      .then(response => {
        const result = response.data.result;

        const RESULT_SUCCESS = 1;
        if(result.code === RESULT_SUCCESS) {
          notifier.showSuccessAfterTransition('Game event created.');
          this.$router.push({name: 'gvnts'});
          return;
        }

        notifier.showError(result.message, {sender: this});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },
};


class GvntDefInputReport {
  /**
   * @param {?GvntDef} gvntDef
   * @param {?Error}   error
   */
  constructor(gvntDef, error) {
    this.gvntDef = gvntDef;
    this.error   = error;
  }
}

class GvntDef {
  /**
   * @param {number}        type
   * @param {number}        plannedStartSec
   * @param {number}        plannedFinishSec
   * @param {string}        clientMinVersion
   * @param {PlayerGroupId} playerGroupId
   * @param {boolean}       startsOnlyOnce
   */
  constructor(
    type,
    plannedStartSec,
    plannedFinishSec,
    clientMinVersion,
    playerGroupId,
    startsOnlyOnce
  ) {
    this.type              = type;
    this.plannedStartSec   = plannedStartSec;
    this.plannedFinishSec  = plannedFinishSec;
    this.clientMinVersion  = clientMinVersion;
    this.playerGroupId     = playerGroupId;
    this.startsOnlyOnce    = startsOnlyOnce;
  }
}
</script>

<style scoped>
.period-table {
  width: 100%;
}

.period-table td:first-child {
  width: 1%;
  padding-right: 0.4em;
}

.period-table td.date {
  padding-right: 0.19em;
}

.period-table > tr:first-child > td,
.period-table > tr:nth-child(2) > td {
  padding-bottom: 0.4em;
}

.client-min-version-range td:first-child {
  padding-right: 0.3rem;
}
</style>
